import { useCallback, useEffect, useRef } from 'react'

/**
 * Fetches the next page when a sentinel scrolls into view.
 *
 * An `IntersectionObserver` fires on the browser's own compositing pass, so it
 * costs nothing while the list sits still, and it needs no reference to
 * whichever ancestor happens to be the scrolling box.
 *
 * The returned ref goes on an empty element at the end of the list, after the
 * last row and before whatever footer the list carries. It is a callback ref:
 * the sentinel usually mounts after the hook does, once the skeleton gives way
 * to rows, and the observer attaches to whichever element is there.
 *
 * `itemCount` is how many rows are loaded. The observer reports a change of
 * visibility only, so a page too short to push the sentinel out of view would
 * end the list there; a count that grew while the sentinel is still visible
 * asks for the next page. A failed fetch adds no rows, so it is not retried.
 */
export function useInfiniteScroll({
  hasNextPage,
  isFetching,
  fetchNextPage,
  itemCount,
}: {
  hasNextPage: boolean
  isFetching: boolean
  fetchNextPage: () => void
  itemCount: number
}) {
  // Mirrored so the observer is not rebuilt on every fetch: re-observing mid
  // scroll drops the intersection that was about to fire.
  const state = useRef({ hasNextPage, isFetching, fetchNextPage, itemCount })
  state.current = { hasNextPage, isFetching, fetchNextPage, itemCount }

  const isVisible = useRef(false)
  /** How many rows were loaded when the last page was asked for. */
  const requestedAt = useRef<number | null>(null)

  const request = useCallback(() => {
    const current = state.current
    if (!current.hasNextPage || current.isFetching) return
    requestedAt.current = current.itemCount
    current.fetchNextPage()
  }, [])

  // React 19 runs a ref callback's return value when the element goes, which
  // is the whole of the observer's lifecycle.
  const setNode = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || typeof IntersectionObserver === 'undefined') return

      const observer = new IntersectionObserver(
        entries => {
          isVisible.current = entries[0]?.isIntersecting ?? false
          if (isVisible.current) request()
        },
        // A page ahead of the edge, so the next one is already arriving by the
        // time the last row is reached.
        { rootMargin: '400px' },
      )
      observer.observe(element)
      return () => {
        observer.disconnect()
        isVisible.current = false
      }
    },
    [request],
  )

  useEffect(() => {
    if (isFetching || !isVisible.current) return
    if (itemCount === requestedAt.current) return
    request()
  }, [itemCount, isFetching, request])

  return setNode
}
